/*
 * Gráfica de barras horizontales del tablero, sobre el motor unificado de
 * gráficas (Echartxx de codekit, FASE 5): ticks en COP, tooltip y
 * animación nativos. Reemplaza la réplica artesanal de Chart.js en CSS.
 */
import { useMemo } from "react";

import { Echartxx } from "codekit";

import { fmtCOP, htmlTheme } from "./utils/carthelp";

/**
 * Renderiza una gráfica de barras horizontales con ejes y gridlines
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Filas [{ label, value, color, raw }]
 * @param {Function} props.onItemClick Callback con la fila y su índice al hacer click
 * @param {number} props.height Alto total del área de la gráfica
 * @param {number} props.labelWidth Ancho de la columna de etiquetas
 */
const Barlistx = ({ data = [], onItemClick, height = 300, labelWidth = 170 }) => {
  const bClickable = typeof onItemClick === "function";

  //ECharts pinta las categorías de abajo hacia arriba: se invierte para
  //conservar el orden visual de la versión anterior (primera fila arriba).
  const mFilas = useMemo(() => [...data].reverse(), [data]);

  const opcion = useMemo(
    () => ({
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const mItem = mFilas[params.dataIndex] ?? {};
          return `<b>${mItem.raw || mItem.label}</b><br/>${fmtCOP(mItem.value)}`;
        },
      },
      grid: { left: 8, right: 24, top: 8, bottom: 4, containLabel: true },
      xAxis: {
        type: "value",
        axisLabel: { fontSize: 10, color: htmlTheme.muted, formatter: (nValor) => fmtCOP(nValor) },
      },
      yAxis: {
        type: "category",
        data: mFilas.map((mItem) => mItem.label),
        axisLabel: {
          fontSize: 11,
          color: htmlTheme.muted,
          overflow: "truncate",
          width: labelWidth,
        },
      },
      series: [
        {
          type: "bar",
          cursor: bClickable ? "pointer" : "default",
          barMaxWidth: 26,
          itemStyle: {
            borderRadius: [0, 6, 6, 0],
            color: (params) => mFilas[params.dataIndex]?.color || htmlTheme.navy,
          },
          emphasis: { itemStyle: { opacity: 0.85 } },
          data: mFilas.map((mItem) => Math.abs(mItem.value)),
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mFilas, labelWidth],
  );

  const eventos = useMemo(
    () => ({
      click: (params) => {
        const mItem = mFilas[params.dataIndex];
        if (bClickable && mItem !== undefined) {
          onItemClick(mItem, data.indexOf(mItem));
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mFilas, onItemClick],
  );

  return <Echartxx option={opcion} onEvents={eventos} height={height} />;
};

export default Barlistx;
